import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Tooltip } from "@merge-api/merge-javascript-shared";
import { fetchCurrentUser, fetchCreateTestLinkToken } from "../../../api-client/APIClient";
import useAppContext from "../../context/useAppContext";
import { UserType } from "../../../models/Entities";
import AddProductionLinkedAccountButton from "./AddProductionLinkedAccountButton";
import AddTestLinkedAccountButton from "./AddTestLinkedAccountButton";

type Props = {
  onRefetch?: (cursor?: any) => void;
  isTestAccounts?: boolean;
};

const CreateLinkedAccountButton = ({ onRefetch, isTestAccounts: isTestAccountsProp }: Props) => {
  const location = useLocation();
  const { user, setUser } = useAppContext();
  const [linkToken, setLinkToken] = useState<string | undefined>();
  const [accountLimitReached, setAccountLimitReached] = useState<boolean>(false);

  const isTestAccounts = isTestAccountsProp ?? location.pathname.includes("test-accounts");
  const isSupportRole = user.type === UserType.support;
  const isFreePlan = user.organization.organization_billing_plan?.billing_plan.plan_tier == "FREE";

  useEffect(() => {
    if (isTestAccounts) {
      fetchCreateTestLinkToken(setLinkToken, setAccountLimitReached);
    }
  }, [isTestAccounts]);

  const handleNewLinkedAccount = useCallback(
    (wasSuccess: boolean) => {
      if (wasSuccess) {
        if (isFreePlan) fetchCurrentUser(setUser);
        if (onRefetch) onRefetch();
      }
      fetchCreateTestLinkToken(setLinkToken, setAccountLimitReached);
    },
    [isFreePlan, onRefetch, setUser],
  );

  // Hide the button if merge link buttons are disabled for this user
  if (user.is_merge_link_button_disabled) {
    return null;
  }

  if (isTestAccounts) {
    if (accountLimitReached) {
      return (
        <Tooltip
          title={
            isSupportRole
              ? "Insufficient permissions. Please contact your Merge administrator to access this feature."
              : "You have reached the limit for test accounts."
          }
        >
          <AddTestLinkedAccountButton
            linkToken={linkToken ?? ""}
            accountLimitReached={accountLimitReached}
            disabled={isSupportRole}
            onNewLinkedAccount={handleNewLinkedAccount}
          />
        </Tooltip>
      );
    } else if (linkToken) {
      return (
        <Tooltip
          title={
            isSupportRole
              ? "Insufficient permissions. Please contact your Merge administrator to access this feature."
              : "Test Linked Accounts do not count towards billing and can only be synced manually through this dashboard"
          }
        >
          <AddTestLinkedAccountButton
            linkToken={linkToken}
            accountLimitReached={accountLimitReached}
            disabled={isSupportRole}
            onNewLinkedAccount={handleNewLinkedAccount}
          />
        </Tooltip>
      );
    } else {
      return null;
    }
  }

  return <AddProductionLinkedAccountButton />;
};

export default CreateLinkedAccountButton;
